import {
  useExchangesMetadata,
  useSymbolsMetadata,
} from "@shared/utils/hooks/misc";
import { ExchangeTag } from "@shared/features/tradingTerminal/common/types";
import { useCallback, useContext, useMemo } from "react";
import { mapValues, pickBy } from "lodash";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import { getPairFromSymbol } from "@shared/utils/misc";
import { getAmountTypeSuffix } from "@shared/features/bots/utils";
import { round } from "@shared/utils/numbers";
import { EXCHANGES, MARGIN_EXCHANGES } from "@shared/common/constants";
import { OverrideType } from "@shared/features/bots/BotForm/common/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

export const useExchangeTags = () => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const tradingTerminalData = useContext(TradingTerminalContext);
  const botFormData = useContext(BotFormContext);
  const { exchanges } = tradingTerminalData ?? botFormData ?? {};
  const { exchangesMetadata, exchanges: allExchanges } = useExchangesMetadata();
  const { isSymbolActiveInExchange } = useSymbolsMetadata();
  const { getInvalidLeverageExchangesData } = useExchangesMaxLeverage();
  const rawLeverage =
    tradingTerminalData?.extraInfo?.leverage ?? botFormData?.multiplier;
  const leverage = isSymbol(rawLeverage) ? undefined : rawLeverage;

  const filteredExchanges = useMemo(
    () => allExchanges.filter((exchange) => exchange !== EXCHANGES.BITMEX),
    [allExchanges]
  );

  const invalidLeverageExchangesData = useMemo(() => {
    return getInvalidLeverageExchangesData(leverage);
  }, [leverage, getInvalidLeverageExchangesData]);

  const TAG_TO_FILTER = {
    [ExchangeTag.FUTURES]: (exchange: string) => {
      const metadata = exchangesMetadata?.[exchange];
      return metadata?.is_inverse_exchange || metadata?.is_margin_exchange;
    },
    [ExchangeTag.SPOT]: (exchange: string) =>
      exchangesMetadata?.[exchange].is_spot_exchange,
  };

  const tagToExchanges = useMemo(() => {
    return Object.values(ExchangeTag).reduce((acc, tag) => {
      acc[tag] = filteredExchanges.filter(TAG_TO_FILTER[tag]);
      return acc;
    }, {} as Record<ExchangeTag, string[]>);
  }, [filteredExchanges]);

  const getSymbolExchangesData = useCallback(
    (symbol?: string) => {
      const isMarginExchanges =
        !isSymbol(exchanges) && MARGIN_EXCHANGES.has(exchanges?.[0] ?? "");
      const tagsExchangeToError = filteredExchanges.reduce((acc, exchange) => {
        if (!symbol) {
          return acc;
        }
        if (!isSymbolActiveInExchange(symbol, exchange)) {
          acc[exchange] = t("messages.errors.symbol_not_available");
        } else if (exchange in invalidLeverageExchangesData) {
          const maxLeverage = invalidLeverageExchangesData[exchange];
          acc[exchange] = t("messages.errors.leverage_exceeds_max", {
            maxLeverage,
          });
        }
        return acc;
      }, {} as Record<string, string>);

      const exchangeToError = filteredExchanges.reduce(
        (acc, exchange) => {
          if (
            !isSymbol(exchanges) &&
            exchanges?.length &&
            MARGIN_EXCHANGES.has(exchange) !== isMarginExchanges
          ) {
            acc[exchange] = t("messages.errors.cannot_mix_futures_spot");
          }
          return acc;
        },
        { ...tagsExchangeToError }
      );

      const tagToValidExchanges = mapValues(tagToExchanges, (exchanges) =>
        exchanges.filter((exchange) => !(exchange in tagsExchangeToError))
      );
      return { exchangeToError, tagToValidExchanges };
    },
    [
      filteredExchanges,
      tagToExchanges,
      isSymbolActiveInExchange,
      invalidLeverageExchangesData,
      exchanges,
      i18n.language,
    ]
  );

  return {
    allExchanges: filteredExchanges,
    getSymbolExchangesData,
  };
};

export const useSummaryAmount = () => {
  const { amount, amountType, isSignal, precisionConfig, symbols, amountCoin } =
    useContext(TradingTerminalContext)!;

  const displayedAmountCoin = amountCoin ?? getPairFromSymbol(symbols[0] || "");
  let amountValue = Number(amount);

  if (isSignal) {
    return amountType
      ? `${amountValue}${getAmountTypeSuffix(amountType, true)}`
      : OverrideType.USER_CONFIGURATION;
  }

  return `${round(Number(amount), precisionConfig?.amount)} ${
    displayedAmountCoin || ""
  }`;
};

export const useExchangesMaxLeverage = () => {
  const tradingTerminalData = useContext(TradingTerminalContext);
  const botFormData = useContext(BotFormContext);
  const { symbols: rawSymbols, exchanges: rawSelectedExchanges } =
    tradingTerminalData ?? botFormData ?? {};
  const selectedExchanges = isSymbol(rawSelectedExchanges)
    ? undefined
    : rawSelectedExchanges;
  const symbols = isSymbol(rawSymbols) ? undefined : rawSymbols;

  const { getSymbolMetadata } = useSymbolsMetadata();
  const { exchanges } = useExchangesMetadata();
  const symbol = symbols?.[0];

  const exchangeToMaxLeverage = useMemo(() => {
    return exchanges?.reduce((acc, exchange) => {
      const exchangeMetadata = getSymbolMetadata(symbol, exchange);
      const maxLeverage = exchangeMetadata?.leverage_max;
      if (maxLeverage) {
        acc[exchange] = maxLeverage;
      }
      return acc;
    }, {} as Record<string, number>);
  }, [symbol, exchanges, getSymbolMetadata]);

  const getInvalidLeverageExchangesData = useCallback(
    (leverage?: number) => {
      if (!leverage) return {};
      return pickBy(
        exchangeToMaxLeverage,
        (maxLeverage) => leverage > maxLeverage
      );
    },
    [exchangeToMaxLeverage]
  );

  const getInvalidLeverageSelectedExchangesData = useCallback(
    (leverage?: number) => {
      if (!leverage) return {};
      return pickBy(exchangeToMaxLeverage, (maxLeverage, exchange) => {
        return leverage > maxLeverage && selectedExchanges?.includes(exchange);
      });
    },
    [exchangeToMaxLeverage, exchanges, selectedExchanges]
  );

  return {
    getInvalidLeverageExchangesData,
    getInvalidLeverageSelectedExchangesData,
  };
};
